
import React, { useState, useEffect } from 'react'
import { X, Plus, Trash2 } from 'lucide-react'

interface AtividadeFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  initialData?: any
  title: string
  obraId: string
  funcionarios: any[]
  insumos: any[]
  etapas: any[]
}

export const AtividadeForm: React.FC<AtividadeFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title,
  obraId,
  funcionarios,
  insumos,
  etapas
}) => {
  const [formData, setFormData] = useState({
    obraId,
    etapaId: '',
    subetapaId: '',
    descricao: '',
    funcionarioResponsavel: '',
    funcionarioId: '',
    prazo: '',
    prioridade: 'media',
    status: 'pendente',
    tipo: 'planejada',
    progresso: 0,
    observacoes: ''
  })

  const [materiaisNecessarios, setMateriaisNecessarios] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (initialData) {
      setFormData({
        obraId: initialData.obraId || obraId,
        etapaId: initialData.etapaId || '',
        subetapaId: initialData.subetapaId || '',
        descricao: initialData.descricao || '',
        funcionarioResponsavel: initialData.funcionarioResponsavel || '',
        funcionarioId: initialData.funcionarioId || '',
        prazo: initialData.prazo ? initialData.prazo.split('T')[0] : '',
        prioridade: initialData.prioridade || 'media',
        status: initialData.status || 'pendente',
        tipo: initialData.tipo || 'planejada',
        progresso: initialData.progresso || 0,
        observacoes: initialData.observacoes || ''
      })
      setMateriaisNecessarios(initialData.materiaisNecessarios || [])
    } else {
      setFormData({
        obraId,
        etapaId: '',
        subetapaId: '',
        descricao: '',
        funcionarioResponsavel: '',
        funcionarioId: '',
        prazo: '',
        prioridade: 'media',
        status: 'pendente',
        tipo: 'planejada',
        progresso: 0,
        observacoes: ''
      })
      setMateriaisNecessarios([])
    }
  }, [initialData, isOpen, obraId])

  const handleFuncionarioChange = (funcionarioId: string) => {
    const funcionario = funcionarios.find(f => f._id === funcionarioId)
    if (funcionario) {
      setFormData(prev => ({
        ...prev,
        funcionarioId,
        funcionarioResponsavel: funcionario.nome
      }))
    }
  }

  const addMaterial = () => {
    setMateriaisNecessarios([...materiaisNecessarios, {
      insumoId: '',
      nome: '',
      quantidade: 0,
      unidade: ''
    }])
  }

  const updateMaterial = (index: number, field: string, value: any) => {
    const updated = [...materiaisNecessarios]
    updated[index] = { ...updated[index], [field]: value }
    
    if (field === 'insumoId') {
      const insumo = insumos.find(i => i._id === value)
      if (insumo) {
        updated[index].nome = insumo.nome
        updated[index].unidade = insumo.unidadeMedida
      }
    }
    
    setMateriaisNecessarios(updated)
  }

  const removeMaterial = (index: number) => {
    setMateriaisNecessarios(materiaisNecessarios.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const submitData = {
        ...formData,
        prazo: formData.prazo ? new Date(formData.prazo).toISOString() : '',
        materiaisNecessarios
      }

      await onSubmit(submitData)
      onClose()
    } catch (error) {
      console.error('Erro ao salvar:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Descrição */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição da Atividade *
              </label>
              <textarea
                value={formData.descricao}
                onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Etapa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Etapa
              </label>
              <select
                value={formData.etapaId}
                onChange={(e) => setFormData(prev => ({ ...prev, etapaId: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione uma etapa</option>
                {etapas.map(etapa => (
                  <option key={etapa.id} value={etapa.id}>
                    {etapa.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Funcionário Responsável */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Funcionário Responsável *
              </label>
              <select
                value={formData.funcionarioId}
                onChange={(e) => handleFuncionarioChange(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Selecione um funcionário</option>
                {funcionarios.map(funcionario => (
                  <option key={funcionario._id} value={funcionario._id}>
                    {funcionario.nome} - {funcionario.cargo}
                  </option>
                ))}
              </select>
            </div>

            {/* Prazo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prazo *
              </label>
              <input
                type="date"
                value={formData.prazo}
                onChange={(e) => setFormData(prev => ({ ...prev, prazo: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Prioridade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prioridade *
              </label>
              <select
                value={formData.prioridade}
                onChange={(e) => setFormData(prev => ({ ...prev, prioridade: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="baixa">Baixa</option>
                <option value="media">Média</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="pendente">Pendente</option>
                <option value="em_andamento">Em Andamento</option>
                <option value="concluida">Concluída</option>
                <option value="atrasada">Atrasada</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>

            {/* Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo *
              </label>
              <select
                value={formData.tipo}
                onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="planejada">Planejada</option>
                <option value="adicional">Adicional</option>
                <option value="corretiva">Corretiva</option>
              </select>
            </div>

            {/* Progresso */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Progresso (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={formData.progresso}
                onChange={(e) => setFormData(prev => ({ ...prev, progresso: parseInt(e.target.value) || 0 }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Observações */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Observações
            </label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Materiais Necessários */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Materiais Necessários</h3>
              <button
                type="button"
                onClick={addMaterial}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center gap-2"
              >
                <Plus size={16} />
                Adicionar Material
              </button>
            </div>

            {materiaisNecessarios.map((material, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium">Material {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeMaterial(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Insumo
                    </label>
                    <select
                      value={material.insumoId}
                      onChange={(e) => updateMaterial(index, 'insumoId', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Selecione um insumo</option>
                      {insumos.map(insumo => (
                        <option key={insumo._id} value={insumo._id}>
                          {insumo.nome}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantidade
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={material.quantidade}
                      onChange={(e) => updateMaterial(index, 'quantidade', parseFloat(e.target.value) || 0)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unidade
                    </label>
                    <input
                      type="text"
                      value={material.unidade}
                      readOnly
                      className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AtividadeForm
